// src/components/pages/SuperAdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getUsersByWorkspace, getSystemMetrics, getRecentActivity, getUserGrowth
} from '../../services/superAdminService';
import { subscribeToWorkspaces, approveWorkspace, rejectWorkspace, suspendWorkspace } from '../../services/workspaceService';
import { changeWorkspacePlan } from '../../services/planService';
import { PLANS, PLAN_ORDER } from '../../config/plans';
import { getMercadoPagoConfig, saveMercadoPagoConfig } from '../../services/mercadoPagoService';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area, Legend
} from 'recharts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';
import {
  FiUsers, FiBriefcase, FiActivity, FiDatabase, FiLoader, FiArrowLeft,
  FiCheck, FiXCircle, FiPause, FiClock, FiTrendingUp, FiGlobe,
  FiPhone, FiMail, FiChevronDown, FiRefreshCw, FiLayers, FiGrid,
  FiMessageSquare, FiCalendar, FiBookOpen, FiUserCheck, FiAlertCircle,
  FiBarChart2, FiPlay
} from 'react-icons/fi';

const ADMIN_EMAIL = 'vcecursos@gmail.com';
const PIE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Data
  const [workspaces, setWorkspaces] = useState([]);
  const [userData, setUserData] = useState({ users: [], byWorkspace: {}, totalUsers: 0 });
  const [metrics, setMetrics] = useState({});
  const [activity, setActivity] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedWs, setExpandedWs] = useState(null);

  const isSuperAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    if (!isSuperAdmin) return;
    const unsub = subscribeToWorkspaces(setWorkspaces);
    loadData();
    return () => unsub();
  }, [isSuperAdmin]);

  const loadData = async () => {
    try {
      const [ud, m, act, gr] = await Promise.all([
        getUsersByWorkspace(),
        getSystemMetrics(),
        getRecentActivity(),
        getUserGrowth(),
      ]);
      setUserData(ud);
      setMetrics(m);
      setActivity(act);
      setGrowth(gr);
    } catch (err) {
      console.error('Error loading metrics:', err);
      toast.error('Error al cargar metricas');
    }
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Datos actualizados');
  };

  const handleApprove = async (wsId) => {
    setUpdatingId(wsId);
    try { await approveWorkspace(wsId); toast.success('Empresa aprobada'); await loadData(); }
    catch { toast.error('Error'); }
    setUpdatingId(null);
  };

  const handleReject = async (wsId) => {
    if (!window.confirm('Rechazar esta empresa?')) return;
    setUpdatingId(wsId);
    try { await rejectWorkspace(wsId); toast.success('Rechazada'); await loadData(); }
    catch { toast.error('Error'); }
    setUpdatingId(null);
  };

  const handleSuspend = async (wsId) => {
    if (!window.confirm('Suspender esta empresa?')) return;
    setUpdatingId(wsId);
    try { await suspendWorkspace(wsId); toast.success('Suspendida'); await loadData(); }
    catch { toast.error('Error'); }
    setUpdatingId(null);
  };

  const handleReactivate = async (wsId) => {
    setUpdatingId(wsId);
    try { await approveWorkspace(wsId); toast.success('Reactivada'); await loadData(); }
    catch { toast.error('Error'); }
    setUpdatingId(null);
  };

  const handleChangePlan = async (wsId, newPlan) => {
    try {
      await changeWorkspacePlan(wsId, newPlan);
      toast.success(`Plan cambiado a ${PLANS[newPlan].name}`);
      await loadData();
    } catch (err) {
      toast.error('Error al cambiar plan');
      console.error(err);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <FiAlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-lg font-bold text-gray-800">Acceso denegado</h2>
          <p className="text-sm text-gray-500 mt-1">Solo el super-admin puede acceder a este panel.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <FiLoader className="animate-spin text-indigo-500 mx-auto mb-3" size={32} />
          <p className="text-sm text-gray-500">Cargando metricas del sistema...</p>
        </div>
      </div>
    );
  }

  // Computed data
  const activeWs = workspaces.filter(w => w.status === 'active');
  const pendingWs = workspaces.filter(w => w.status === 'pending');
  const suspendedWs = workspaces.filter(w => w.status === 'suspended');
  const activeUsers = userData.users.filter(u => u.status === 'active');
  const pendingUsersCount = userData.users.filter(u => u.status === 'pending').length;

  // Pie data for workspace status
  const wsPieData = [
    { name: 'Activas', value: activeWs.length },
    { name: 'Pendientes', value: pendingWs.length },
    { name: 'Suspendidas', value: suspendedWs.length },
    { name: 'Rechazadas', value: workspaces.filter(w => w.status === 'rejected').length },
  ].filter(d => d.value > 0);

  // Industry distribution
  const industryData = {};
  workspaces.forEach(w => {
    const ind = w.industry || 'Otro';
    industryData[ind] = (industryData[ind] || 0) + 1;
  });
  const industryChart = Object.entries(industryData).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

  // Country distribution
  const countryData = {};
  workspaces.forEach(w => {
    const c = w.country || 'No especificado';
    countryData[c] = (countryData[c] || 0) + 1;
  });
  const countryChart = Object.entries(countryData).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

  // Users per workspace for bar chart
  const usersPerWs = workspaces
    .filter(w => w.status === 'active')
    .map(w => ({
      name: (w.name || '').substring(0, 15),
      usuarios: userData.byWorkspace[w.id]?.total || 1,
    }))
    .sort((a, b) => b.usuarios - a.usuarios)
    .slice(0, 10);

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: FiBarChart2 },
    { id: 'workspaces', label: 'Empresas', icon: FiBriefcase, badge: pendingWs.length },
    { id: 'activity', label: 'Actividad', icon: FiActivity },
    { id: 'config', label: 'Configuracion', icon: FiDatabase },
  ];

  const StatusBadge = ({ status }) => {
    const s = {
      pending: 'bg-amber-100 text-amber-700',
      active: 'bg-emerald-100 text-emerald-700',
      suspended: 'bg-gray-100 text-gray-600',
      rejected: 'bg-red-100 text-red-700',
    };
    const l = { pending: 'Pendiente', active: 'Activa', suspended: 'Suspendida', rejected: 'Rechazada' };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s[status] || s.pending}`}>{l[status] || status}</span>;
  };

  return (
    <div className="h-full bg-gray-50 overflow-y-auto">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/admin')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
                <FiArrowLeft size={18} />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FiLayers className="text-indigo-600" size={20} />
                  Panel Super Admin
                </h1>
                <p className="text-xs text-gray-500">Vision global de todas las empresas y metricas</p>
              </div>
            </div>
            <button onClick={handleRefresh} disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
              <FiRefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-4 -mb-px">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-all ${
                  activeTab === t.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <t.icon size={14} />
                {t.label}
                {t.badge > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full text-[10px] font-bold">{t.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">

        {/* ══════════════ TAB: OVERVIEW ══════════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Pending alert */}
            {pendingWs.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FiClock className="text-amber-500" size={20} />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">{pendingWs.length} empresa(s) esperando aprobacion</p>
                    <p className="text-xs text-amber-600 mt-0.5">Revisa las solicitudes en la pestana "Empresas"</p>
                  </div>
                </div>
                <button onClick={() => setActiveTab('workspaces')} className="px-4 py-2 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors">
                  Revisar
                </button>
              </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Empresas activas', value: activeWs.length, total: workspaces.length, icon: FiBriefcase, color: 'indigo', sub: `${workspaces.length} total` },
                { label: 'Usuarios totales', value: userData.totalUsers, icon: FiUsers, color: 'emerald', sub: `${activeUsers.length} activos` },
                { label: 'Pendientes', value: pendingWs.length + pendingUsersCount, icon: FiClock, color: 'amber', sub: `${pendingWs.length} empresas, ${pendingUsersCount} usuarios` },
                { label: 'Colecciones', value: Object.values(metrics).reduce((a, m) => a + m.count, 0), icon: FiDatabase, color: 'purple', sub: `${Object.keys(metrics).length} colecciones` },
              ].map(kpi => (
                <div key={kpi.label} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-500 font-medium">{kpi.label}</span>
                    <div className={`w-9 h-9 rounded-xl bg-${kpi.color}-50 flex items-center justify-center`}>
                      <kpi.icon size={16} className={`text-${kpi.color}-500`} />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-gray-900">{kpi.value.toLocaleString()}</div>
                  <p className="text-[11px] text-gray-400 mt-1">{kpi.sub}</p>
                </div>
              ))}
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Growth chart */}
              <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FiTrendingUp size={16} className="text-indigo-500" />
                  Crecimiento (ultimos 6 meses)
                </h3>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={growth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="users" name="Usuarios" stroke="#6366f1" fill="#6366f1" fillOpacity={0.1} strokeWidth={2} />
                    <Area type="monotone" dataKey="workspaces" name="Empresas" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Workspace status pie */}
              <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FiBriefcase size={16} className="text-indigo-500" />
                  Estado de empresas
                </h3>
                {wsPieData.length > 0 ? (
                  <div className="flex items-center gap-6">
                    <ResponsiveContainer width="50%" height={200}>
                      <PieChart>
                        <Pie data={wsPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                          {wsPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                      {wsPieData.map((d, i) => (
                        <div key={d.name} className="flex items-center gap-2 text-xs">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <span className="text-gray-600">{d.name}</span>
                          <span className="font-bold text-gray-800 ml-auto">{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">Sin datos aun</p>
                )}
              </div>
            </div>

            {/* Second row of charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Users per workspace */}
              <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm lg:col-span-2">
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FiUsers size={16} className="text-emerald-500" />
                  Usuarios por empresa (top 10)
                </h3>
                {usersPerWs.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={usersPerWs} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#6b7280' }} width={100} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Bar dataKey="usuarios" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">Sin empresas activas</p>
                )}
              </div>

              {/* System metrics */}
              <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FiDatabase size={16} className="text-purple-500" />
                  Datos del sistema
                </h3>
                <div className="space-y-3">
                  {Object.values(metrics).map(m => (
                    <div key={m.label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <span className="text-xs text-gray-600">{m.label}</span>
                      <span className="text-sm font-bold text-gray-800">{m.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Distribution charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* By industry */}
              <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FiGrid size={16} className="text-cyan-500" />
                  Por industria
                </h3>
                {industryChart.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={industryChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Bar dataKey="value" name="Empresas" fill="#06b6d4" radius={[4, 4, 0, 0]} barSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-gray-400 text-center py-8">Sin datos</p>}
              </div>

              {/* By country */}
              <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FiGlobe size={16} className="text-rose-500" />
                  Por pais
                </h3>
                {countryChart.length > 0 ? (
                  <div className="flex items-center gap-6">
                    <ResponsiveContainer width="50%" height={200}>
                      <PieChart>
                        <Pie data={countryChart} cx="50%" cy="50%" outerRadius={75} dataKey="value" paddingAngle={2}>
                          {countryChart.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2 flex-1">
                      {countryChart.map((d, i) => (
                        <div key={d.name} className="flex items-center gap-2 text-xs">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <span className="text-gray-600 truncate">{d.name}</span>
                          <span className="font-bold text-gray-800 ml-auto">{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <p className="text-sm text-gray-400 text-center py-8">Sin datos</p>}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════ TAB: WORKSPACES ══════════════ */}
        {activeTab === 'workspaces' && (
          <div className="space-y-4">
            {/* Pending workspaces first */}
            {pendingWs.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-amber-800 flex items-center gap-2">
                  <FiClock size={14} /> Pendientes de aprobacion ({pendingWs.length})
                </h3>
                {pendingWs.map(ws => (
                  <WorkspaceCard key={ws.id} ws={ws} userData={userData} updatingId={updatingId}
                    expandedWs={expandedWs} setExpandedWs={setExpandedWs}
                    onApprove={handleApprove} onReject={handleReject} onSuspend={handleSuspend} onReactivate={handleReactivate}
                    onChangePlan={handleChangePlan} StatusBadge={StatusBadge} highlight />
                ))}
              </div>
            )}

            {/* All workspaces */}
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mt-6">
              <FiBriefcase size={14} /> Todas las empresas ({workspaces.length})
            </h3>
            {workspaces.filter(w => w.status !== 'pending').map(ws => (
              <WorkspaceCard key={ws.id} ws={ws} userData={userData} updatingId={updatingId}
                expandedWs={expandedWs} setExpandedWs={setExpandedWs}
                onApprove={handleApprove} onReject={handleReject} onSuspend={handleSuspend} onReactivate={handleReactivate}
                onChangePlan={handleChangePlan} StatusBadge={StatusBadge} />
            ))}
          </div>
        )}

        {/* ══════════════ TAB: ACTIVITY ══════════════ */}
        {activeTab === 'activity' && (
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <FiActivity size={14} /> Actividad reciente
            </h3>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
              {activity.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">Sin actividad reciente</div>
              ) : activity.map((act, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    act.type === 'workspace_created' ? 'bg-indigo-50 text-indigo-500' : 'bg-emerald-50 text-emerald-500'
                  }`}>
                    {act.type === 'workspace_created' ? <FiBriefcase size={16} /> : <FiUserCheck size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800">{act.label}</p>
                    <p className="text-[11px] text-gray-400">{act.email}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <StatusBadge status={act.status} />
                    <p className="text-[10px] text-gray-400 mt-1">
                      {act.date?.toDate ? format(act.date.toDate(), 'dd MMM yyyy HH:mm', { locale: es }) : '—'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══════════════ TAB: CONFIG ══════════════ */}
        {activeTab === 'config' && (
          <MercadoPagoConfigSection />
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// MercadoPago Config Section
// ═══════════════════════════════════════
const MercadoPagoConfigSection = () => {
  const [config, setConfig] = useState({ publicKey: '', sandbox: true, webhookConfigured: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const cfg = await getMercadoPagoConfig();
      setConfig(cfg);
      setLoading(false);
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveMercadoPagoConfig(config);
      toast.success('Configuracion guardada');
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><FiLoader className="animate-spin text-indigo-500" size={24} /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-gray-800">Configuracion de Pagos</h3>
        <p className="text-xs text-gray-500">Configura MercadoPago para recibir pagos de suscripciones</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-blue-600" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-bold text-gray-800">MercadoPago</h4>
            <p className="text-[11px] text-gray-400">Pasarela de pago para Latinoamerica</p>
          </div>
          <div className="ml-auto">
            <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${config.publicKey ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
              {config.publicKey ? 'Configurado' : 'Sin configurar'}
            </span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">Public Key</label>
          <input
            type="text"
            value={config.publicKey}
            onChange={e => setConfig(prev => ({ ...prev, publicKey: e.target.value }))}
            placeholder="APP_USR-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
          <p className="text-[10px] text-gray-400 mt-1">Obtener en: Mercado Pago Developers → Credenciales</p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <p className="text-xs text-amber-800 font-medium">Access Token (Backend)</p>
          <p className="text-[11px] text-amber-600 mt-0.5">
            El Access Token se configura en Firebase Cloud Functions por seguridad.
            Ejecuta: <code className="bg-amber-100 px-1.5 py-0.5 rounded text-[10px]">firebase functions:config:set mercadopago.access_token="YOUR_TOKEN"</code>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.sandbox}
              onChange={e => setConfig(prev => ({ ...prev, sandbox: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-xs text-gray-700">Modo Sandbox (pruebas)</span>
          </label>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.webhookConfigured}
              onChange={e => setConfig(prev => ({ ...prev, webhookConfigured: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-xs text-gray-700">Webhook configurado en MercadoPago</span>
          </label>
        </div>

        <button onClick={handleSave} disabled={saving}
          className="px-6 py-2.5 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2">
          {saving ? <FiLoader className="animate-spin" size={14} /> : <FiCheck size={14} />}
          Guardar configuracion
        </button>
      </div>

      {/* Setup guide */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
        <h4 className="text-sm font-bold text-gray-800 mb-3">Guia de configuracion</h4>
        <ol className="space-y-3 text-xs text-gray-600">
          {[
            'Crear cuenta en mercadopago.com.pe/developers',
            'Crear una aplicacion en el dashboard de desarrolladores',
            'Copiar la Public Key y pegarla arriba',
            'Configurar el Access Token en Firebase Cloud Functions',
            'Desplegar la Cloud Function de webhook',
            'Configurar la URL del webhook en MercadoPago',
            'Probar con credenciales de sandbox',
            'Cambiar a produccion cuando estes listo',
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
              {step}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// WorkspaceCard component
// ═══════════════════════════════════════
const WorkspaceCard = ({ ws, userData, updatingId, expandedWs, setExpandedWs, onApprove, onReject, onSuspend, onReactivate, onChangePlan, StatusBadge, highlight }) => {
  const wsUsers = userData.byWorkspace[ws.id] || { total: 0, active: 0 };
  const [changingPlan, setChangingPlan] = useState(false);

  return (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${highlight ? 'border-amber-200 ring-1 ring-amber-100' : 'border-gray-100'}`}>
      <div className="flex items-center gap-4 px-5 py-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-lg font-bold text-indigo-600 flex-shrink-0">
          {(ws.name || '?')[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-gray-800 truncate">{ws.name}</h3>
            <StatusBadge status={ws.status} />
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-400 flex-wrap">
            <span className="flex items-center gap-1"><FiMail size={10} />{ws.ownerEmail}</span>
            {ws.country && <span className="flex items-center gap-1"><FiGlobe size={10} />{ws.country}</span>}
            <span className="flex items-center gap-1"><FiUsers size={10} />{wsUsers.total} usuario(s)</span>
            {ws.industry && <span className="flex items-center gap-1"><FiGrid size={10} />{ws.industry}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {ws.status === 'pending' && (
            <>
              <button onClick={() => onApprove(ws.id)} disabled={updatingId === ws.id}
                className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
                {updatingId === ws.id ? <FiLoader className="animate-spin" size={12} /> : <FiCheck size={12} />} Aprobar
              </button>
              <button onClick={() => onReject(ws.id)} disabled={updatingId === ws.id}
                className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-medium rounded-lg hover:bg-red-100 disabled:opacity-50 flex items-center gap-1">
                <FiXCircle size={12} /> Rechazar
              </button>
            </>
          )}
          {ws.status === 'active' && (
            <button onClick={() => onSuspend(ws.id)} disabled={updatingId === ws.id}
              className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1">
              <FiPause size={12} /> Suspender
            </button>
          )}
          {(ws.status === 'suspended' || ws.status === 'rejected') && (
            <button onClick={() => onReactivate(ws.id)} disabled={updatingId === ws.id}
              className="px-3 py-1.5 bg-emerald-50 text-emerald-600 text-xs font-medium rounded-lg hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1">
              <FiPlay size={12} /> Reactivar
            </button>
          )}
          <button onClick={() => setExpandedWs(expandedWs === ws.id ? null : ws.id)}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <FiChevronDown size={14} className={`transition-transform ${expandedWs === ws.id ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {expandedWs === ws.id && (
        <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px]">
            <div><span className="text-gray-400 block">RUC</span><span className="text-gray-700 font-medium">{ws.ruc || '—'}</span></div>
            <div><span className="text-gray-400 block">Telefono</span><span className="text-gray-700 font-medium">{ws.phone || '—'}</span></div>
            <div><span className="text-gray-400 block">Tamano</span><span className="text-gray-700 font-medium">{ws.size || '—'}</span></div>
            <div>
              <span className="text-gray-400 block mb-1">Plan</span>
              <div className="flex items-center gap-1.5">
                {PLAN_ORDER.map(p => {
                  const planCfg = PLANS[p];
                  const isCurrent = (ws.plan || 'free') === p;
                  return (
                    <button
                      key={p}
                      disabled={changingPlan || isCurrent}
                      onClick={async () => {
                        setChangingPlan(true);
                        await onChangePlan(ws.id, p);
                        setChangingPlan(false);
                      }}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold transition-all ${
                        isCurrent
                          ? `${planCfg.badge} ring-1 ring-current`
                          : 'bg-gray-50 text-gray-400 hover:bg-gray-100 cursor-pointer'
                      } ${changingPlan ? 'opacity-50' : ''}`}
                    >
                      {planCfg.name}
                    </button>
                  );
                })}
              </div>
              <span className="text-[10px] text-gray-400 mt-0.5 block">Max: {PLANS[ws.plan || 'free'].maxUsers === Infinity ? '∞' : PLANS[ws.plan || 'free'].maxUsers} usuarios</span>
            </div>
            <div><span className="text-gray-400 block">Usuarios activos</span><span className="text-emerald-600 font-bold">{wsUsers.active}</span></div>
            <div><span className="text-gray-400 block">Usuarios pendientes</span><span className="text-amber-600 font-bold">{wsUsers.pending || 0}</span></div>
            <div><span className="text-gray-400 block">Onboarding</span><span className={ws.onboardingCompleted ? 'text-emerald-600 font-medium' : 'text-amber-600 font-medium'}>{ws.onboardingCompleted ? 'Completado' : 'Pendiente'}</span></div>
            <div><span className="text-gray-400 block">Registro</span><span className="text-gray-700 font-medium">{ws.createdAt?.toDate ? format(ws.createdAt.toDate(), 'dd MMM yyyy', { locale: es }) : '—'}</span></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
